import { useState, useEffect } from 'react';
import { getInitialBoard } from '../utils/chess/board';
import { getValidMoves } from '../utils/chess/moves';
import { isCheckmate, isStalemate } from '../utils/chess/validation';
import { makeMove } from '../utils/chess/core';
import type { ChessBoard, ChessPosition, ChessMove } from '../types/chess';

export function useChessGame(
  difficulty: 'easy' | 'medium' | 'hard',
  onWin: () => void,
  onLose: () => void,
  onDraw: () => void
) {
  const [board, setBoard] = useState<ChessBoard>(getInitialBoard());
  const [selectedPiece, setSelectedPiece] = useState<ChessPosition | null>(null);
  const [validMoves, setValidMoves] = useState<ChessPosition[]>([]);
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const [lastMove, setLastMove] = useState<ChessMove | null>(null);
  const [moveHistory, setMoveHistory] = useState<ChessMove[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Update valid moves when a piece is selected
  useEffect(() => {
    if (selectedPiece) {
      const moves = getValidMoves({ board, position: selectedPiece });
      setValidMoves(moves);
    } else {
      setValidMoves([]);
    }
  }, [selectedPiece, board]);

  const handleMove = (from: ChessPosition, to: ChessPosition) => {
    if (!isPlayerTurn || gameOver) return;

    const piece = board[from.row][from.col];
    if (!piece) return;

    const newBoard = makeMove(board, from, to);
    const move: ChessMove = {
      from,
      to,
      piece,
      captured: board[to.row][to.col]
    };

    setBoard(newBoard);
    setLastMove(move);
    setMoveHistory(prev => [...prev, move]);
    setSelectedPiece(null);

    // Check game state
    if (isCheckmate(newBoard, 'black')) {
      setGameOver(true);
      onWin();
      return;
    }

    if (isStalemate(newBoard, 'black')) {
      setGameOver(true);
      onDraw();
      return;
    }

    setIsPlayerTurn(false);
    setIsProcessing(true);
  };

  // AI move handling
  useEffect(() => {
    if (!isPlayerTurn && !gameOver) {
      const timer = setTimeout(() => {
        // Simplified AI move for now - just make a random valid move
        let validAIMoves: { from: ChessPosition; to: ChessPosition; }[] = [];
        
        // Collect all possible moves for black pieces
        for (let row = 0; row < 8; row++) {
          for (let col = 0; col < 8; col++) {
            const piece = board[row][col];
            if (piece?.color === 'black') {
              const moves = getValidMoves({ board, position: { row, col } });
              moves.forEach(to => {
                validAIMoves.push({ from: { row, col }, to });
              });
            }
          }
        }

        if (validAIMoves.length > 0) {
          const moveIndex = Math.floor(Math.random() * validAIMoves.length);
          const aiMove = validAIMoves[moveIndex];
          const newBoard = makeMove(board, aiMove.from, aiMove.to);
          
          const move: ChessMove = {
            from: aiMove.from,
            to: aiMove.to,
            piece: board[aiMove.from.row][aiMove.from.col]!,
            captured: board[aiMove.to.row][aiMove.to.col]
          };

          setBoard(newBoard);
          setLastMove(move);
          setMoveHistory(prev => [...prev, move]);

          if (isCheckmate(newBoard, 'white')) {
            setGameOver(true);
            onLose();
          } else if (isStalemate(newBoard, 'white')) {
            setGameOver(true);
            onDraw();
          } else {
            setIsPlayerTurn(true);
          }
        }
        
        setIsProcessing(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isPlayerTurn, gameOver, board, onWin, onLose, onDraw]);

  return {
    board,
    selectedPiece,
    validMoves,
    isPlayerTurn,
    gameOver,
    lastMove,
    moveHistory,
    isProcessing,
    setSelectedPiece,
    makeMove: handleMove
  };
}